import {
  defineMessages,
  type IntlShape,
  type MessageDescriptor,
} from 'react-intl'

export const CONTACT_FORM_SUBJECT_KEY = {
  ADVERTISING: 'advertising',
  CANCEL_CONTRACT: 'cancelContract',
  CHANGE_ADDRESS: 'changeAddress',
  CHANGE_NAME: 'changeName',
  CHANGE_ORDER: 'changeOrder',
  COPAYMENT_EXEMPTION: 'copaymentExemption',
  DEFER_PAYMENT: 'deferPayment',
  DELETE_ACCOUNT: 'deleteAccount',
  DOCTOR_ORDER: 'doctorOrder',
  INVOICE_STATUS: 'invoiceStatus',
  MISSING_INVOICE: 'missingInvoice',
  ORDER_CANCELLATION: 'orderCancellation',
  ORDER_MISSING_OR_INCORRECT_ITEM: 'orderMissingOrIncorrectItem',
  OTHER_CONCERN: 'otherConcern',
  PAYMENT_REMINDER: 'paymentReminder',
  PHARMA_ADVICE: 'pharmaAdvice',
  PRODUCT_INQUIRY: 'productInquiry',
  REPORT_ORDER: 'reportOrder',
  RETURN_ORDER: 'returnOrder',
  TRACK_DELIVERY_ORDER: 'trackDeliveryOrder',
} as const

export type ContactFormSubjectKey =
  (typeof CONTACT_FORM_SUBJECT_KEY)[keyof typeof CONTACT_FORM_SUBJECT_KEY]

const contactFormSubjectMessages = defineMessages<ContactFormSubjectKey>({
  [CONTACT_FORM_SUBJECT_KEY.ADVERTISING]: {
    id: 'direct.contact.form.advertising',
  },
  [CONTACT_FORM_SUBJECT_KEY.CANCEL_CONTRACT]: {
    id: 'direct.contact.form.cancel.contract',
  },
  [CONTACT_FORM_SUBJECT_KEY.CHANGE_ADDRESS]: {
    id: 'direct.contact.form.change.address',
  },
  [CONTACT_FORM_SUBJECT_KEY.CHANGE_NAME]: {
    id: 'direct.contact.form.change.name',
  },
  [CONTACT_FORM_SUBJECT_KEY.CHANGE_ORDER]: {
    id: 'direct.contact.form.change.order',
  },
  [CONTACT_FORM_SUBJECT_KEY.COPAYMENT_EXEMPTION]: {
    id: 'prescription.options.copaymentExempt',
  },
  [CONTACT_FORM_SUBJECT_KEY.DEFER_PAYMENT]: {
    id: 'direct.contact.form.defer.payment',
  },
  [CONTACT_FORM_SUBJECT_KEY.DELETE_ACCOUNT]: {
    id: 'direct.contact.form.delete.account',
  },
  [CONTACT_FORM_SUBJECT_KEY.DOCTOR_ORDER]: {
    id: 'direct.contact.form.doctor.order',
  },
  [CONTACT_FORM_SUBJECT_KEY.INVOICE_STATUS]: {
    id: 'direct.contact.form.invoice.status',
  },
  [CONTACT_FORM_SUBJECT_KEY.MISSING_INVOICE]: {
    id: 'direct.contact.form.missing.invoice',
  },
  [CONTACT_FORM_SUBJECT_KEY.ORDER_CANCELLATION]: {
    id: 'direct.contact.form.order.cancellation',
  },
  [CONTACT_FORM_SUBJECT_KEY.ORDER_MISSING_OR_INCORRECT_ITEM]: {
    id: 'direct.contact.form.order.missing.or.incorrect.item',
  },
  [CONTACT_FORM_SUBJECT_KEY.OTHER_CONCERN]: {
    id: 'direct.contact.form.other.concern',
  },
  [CONTACT_FORM_SUBJECT_KEY.PAYMENT_REMINDER]: {
    id: 'direct.contact.form.payment.reminder',
  },
  [CONTACT_FORM_SUBJECT_KEY.PHARMA_ADVICE]: {
    id: 'direct.contact.form.pharma.advice',
  },
  [CONTACT_FORM_SUBJECT_KEY.PRODUCT_INQUIRY]: {
    id: 'contactForm.select.item.productInquiry',
  },
  [CONTACT_FORM_SUBJECT_KEY.REPORT_ORDER]: {
    id: 'direct.contact.form.report.order',
  },
  [CONTACT_FORM_SUBJECT_KEY.RETURN_ORDER]: {
    id: 'direct.contact.form.return.order',
  },
  [CONTACT_FORM_SUBJECT_KEY.TRACK_DELIVERY_ORDER]: {
    id: 'direct.contact.form.track.delivery.order',
  },
})

type ContactFormSubjectCatalogEntry = {
  message: MessageDescriptor
  qaId?: string
}

export const CONTACT_FORM_SUBJECT_CATALOG: Record<
  ContactFormSubjectKey,
  ContactFormSubjectCatalogEntry
> = {
  [CONTACT_FORM_SUBJECT_KEY.ADVERTISING]: {
    message: contactFormSubjectMessages.advertising,
    qaId: 'direct-contact-form-advertising',
  },
  [CONTACT_FORM_SUBJECT_KEY.CANCEL_CONTRACT]: {
    message: contactFormSubjectMessages.cancelContract,
  },
  [CONTACT_FORM_SUBJECT_KEY.CHANGE_ADDRESS]: {
    message: contactFormSubjectMessages.changeAddress,
    qaId: 'direct-contact-form-change-address',
  },
  [CONTACT_FORM_SUBJECT_KEY.CHANGE_NAME]: {
    message: contactFormSubjectMessages.changeName,
    qaId: 'direct-contact-form-change-name',
  },
  [CONTACT_FORM_SUBJECT_KEY.CHANGE_ORDER]: {
    message: contactFormSubjectMessages.changeOrder,
    qaId: 'direct-contact-form-change-order',
  },
  [CONTACT_FORM_SUBJECT_KEY.COPAYMENT_EXEMPTION]: {
    message: contactFormSubjectMessages.copaymentExemption,
    qaId: 'direct-contact-form-copayment-exemption',
  },
  [CONTACT_FORM_SUBJECT_KEY.DEFER_PAYMENT]: {
    message: contactFormSubjectMessages.deferPayment,
    qaId: 'direct-contact-form-defer-payment',
  },
  [CONTACT_FORM_SUBJECT_KEY.DELETE_ACCOUNT]: {
    message: contactFormSubjectMessages.deleteAccount,
    qaId: 'direct-contact-form-delete-account',
  },
  [CONTACT_FORM_SUBJECT_KEY.DOCTOR_ORDER]: {
    message: contactFormSubjectMessages.doctorOrder,
    qaId: 'direct-contact-form-doctor-order',
  },
  [CONTACT_FORM_SUBJECT_KEY.INVOICE_STATUS]: {
    message: contactFormSubjectMessages.invoiceStatus,
    qaId: 'direct-contact-form-invoice-status',
  },
  [CONTACT_FORM_SUBJECT_KEY.MISSING_INVOICE]: {
    message: contactFormSubjectMessages.missingInvoice,
    qaId: 'direct-contact-form-missing-invoice',
  },
  [CONTACT_FORM_SUBJECT_KEY.ORDER_CANCELLATION]: {
    message: contactFormSubjectMessages.orderCancellation,
    qaId: 'direct-contact-form-order-cancellation',
  },
  [CONTACT_FORM_SUBJECT_KEY.ORDER_MISSING_OR_INCORRECT_ITEM]: {
    message: contactFormSubjectMessages.orderMissingOrIncorrectItem,
    qaId: 'direct-contact-form-order-missing-or-incorrect-item',
  },
  [CONTACT_FORM_SUBJECT_KEY.OTHER_CONCERN]: {
    message: contactFormSubjectMessages.otherConcern,
    qaId: 'direct-contact-form-other-concern',
  },
  [CONTACT_FORM_SUBJECT_KEY.PAYMENT_REMINDER]: {
    message: contactFormSubjectMessages.paymentReminder,
    qaId: 'direct-contact-form-payment-reminder',
  },
  [CONTACT_FORM_SUBJECT_KEY.PHARMA_ADVICE]: {
    message: contactFormSubjectMessages.pharmaAdvice,
    qaId: 'direct-contact-form-pharma-advice',
  },
  [CONTACT_FORM_SUBJECT_KEY.PRODUCT_INQUIRY]: {
    message: contactFormSubjectMessages.productInquiry,
    qaId: 'direct-contact-form-product-inquiry',
  },
  [CONTACT_FORM_SUBJECT_KEY.REPORT_ORDER]: {
    message: contactFormSubjectMessages.reportOrder,
    qaId: 'direct-contact-form-report-order',
  },
  [CONTACT_FORM_SUBJECT_KEY.RETURN_ORDER]: {
    message: contactFormSubjectMessages.returnOrder,
    qaId: 'direct-contact-form-return-order',
  },
  [CONTACT_FORM_SUBJECT_KEY.TRACK_DELIVERY_ORDER]: {
    message: contactFormSubjectMessages.trackDeliveryOrder,
    qaId: 'direct-contact-form-track-delivery-order',
  },
}

const CONTACT_FORM_ORDER_SUBJECT_KEYS = [
  CONTACT_FORM_SUBJECT_KEY.CHANGE_ORDER,
  CONTACT_FORM_SUBJECT_KEY.TRACK_DELIVERY_ORDER,
  CONTACT_FORM_SUBJECT_KEY.RETURN_ORDER,
  CONTACT_FORM_SUBJECT_KEY.ORDER_CANCELLATION,
  CONTACT_FORM_SUBJECT_KEY.ORDER_MISSING_OR_INCORRECT_ITEM,
  CONTACT_FORM_SUBJECT_KEY.REPORT_ORDER,
  CONTACT_FORM_SUBJECT_KEY.PAYMENT_REMINDER,
  CONTACT_FORM_SUBJECT_KEY.MISSING_INVOICE,
  CONTACT_FORM_SUBJECT_KEY.DEFER_PAYMENT,
  CONTACT_FORM_SUBJECT_KEY.INVOICE_STATUS,
] as const satisfies readonly ContactFormSubjectKey[]

export const CONTACT_FORM_PHARMACEUTICAL_SUBJECT_KEYS = [
  CONTACT_FORM_SUBJECT_KEY.PHARMA_ADVICE,
  CONTACT_FORM_SUBJECT_KEY.PRODUCT_INQUIRY,
  CONTACT_FORM_SUBJECT_KEY.DOCTOR_ORDER,
] as const satisfies readonly ContactFormSubjectKey[]

export const CONTACT_FORM_FULL_SUBJECT_KEYS = [
  ...CONTACT_FORM_ORDER_SUBJECT_KEYS,
  CONTACT_FORM_SUBJECT_KEY.CHANGE_NAME,
  CONTACT_FORM_SUBJECT_KEY.CHANGE_ADDRESS,
  CONTACT_FORM_SUBJECT_KEY.ADVERTISING,
  CONTACT_FORM_SUBJECT_KEY.DELETE_ACCOUNT,
  ...CONTACT_FORM_PHARMACEUTICAL_SUBJECT_KEYS,
  CONTACT_FORM_SUBJECT_KEY.COPAYMENT_EXEMPTION,
  CONTACT_FORM_SUBJECT_KEY.OTHER_CONCERN,
] as const satisfies readonly ContactFormSubjectKey[]

export const directContactFormSubjectCatalogGetValue = (
  intl: IntlShape,
  subjectKey: ContactFormSubjectKey,
): string =>
  intl.formatMessage(CONTACT_FORM_SUBJECT_CATALOG[subjectKey].message)
